import { useCallback, useEffect, useMemo, useState } from 'react';
import { Camera, RefreshCcw, ScanFace, ShieldCheck, TabletSmartphone } from 'lucide-react';
import toast from 'react-hot-toast';
import { attendanceAPI, createAuthenticatedWebSocket } from '../services/api';

const FaceAttendance = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const profilesRes = await attendanceAPI.getFaceProfiles();
      setProfiles(profilesRes.data.profiles || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load face directory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let socket;
    let reconnectTimer;
    let closed = false;

    const connect = () => {
      socket = createAuthenticatedWebSocket();

      socket.onopen = () => setLiveConnected(true);

      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data);
          if (event.type === 'FACE_PROFILES_CHANGED') {
            loadData();
            toast.success('Face directory synced');
          }
        } catch {
          // Ignore malformed socket payloads.
        }
      };

      socket.onerror = () => setLiveConnected(false);

      socket.onclose = () => {
        setLiveConnected(false);
        if (!closed) reconnectTimer = setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) socket.close();
    };
  }, [loadData]);

  const stats = useMemo(() => ({
    enrolled: profiles.length,
    withImages: profiles.filter((profile) => profile.face_image_data).length,
    descriptors: profiles.filter((profile) => profile.face_descriptor).length,
  }), [profiles]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-500">SmartCampus face directory</p>
          <h1 className="mt-2 text-2xl font-bold text-slate-950">Face Attendance Directory</h1>
          <p className="mt-1 text-sm text-slate-500">
            Registration, replacement, and deletion happen on the tablet with admin access. Web is a read-only directory.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm">
            <span className={`h-2 w-2 rounded-full ${liveConnected ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {liveConnected ? 'Live sync on' : 'Reconnecting'}
          </span>
          <button
            type="button"
            onClick={loadData}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Metric icon={ScanFace} label="Registered Faces" value={stats.enrolled} />
        <Metric icon={Camera} label="Stored Images" value={stats.withImages} />
        <Metric icon={ShieldCheck} label="Descriptors" value={stats.descriptors} />
        <Metric icon={TabletSmartphone} label="Registration Device" value="Tablet" />
      </div>

      <section className="rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-950">Registered Face Profiles</h2>
          <p className="mt-1 text-sm text-slate-500">These profiles are synced to tablet recognition terminals.</p>
        </div>
        <div className="divide-y divide-slate-100">
          {loading ? (
            <p className="p-5 text-sm text-slate-500">Loading directory...</p>
          ) : profiles.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No face profiles have been registered yet.</p>
          ) : (
            profiles.map((profile) => (
              <div key={profile.id} className="grid gap-3 px-5 py-4 md:grid-cols-[1fr_auto] md:items-center">
                <div className="flex items-center gap-3">
                  {profile.face_image_data ? (
                    <img
                      src={profile.face_image_data}
                      alt={`${profile.full_name} registered face`}
                      className="h-16 w-16 shrink-0 rounded-2xl object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-400">
                      Face
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-950">{profile.full_name}</p>
                    <p className="text-sm text-slate-500">
                      {[profile.employee_id, profile.role, profile.department_name, profile.section_name].filter(Boolean).join(' - ')}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-500">
                  {profile.face_enrolled_at ? new Date(profile.face_enrolled_at).toLocaleString() : 'Registered'}
                </p>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value }) => (
  <article className="rounded-[1.25rem] border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
        <Icon size={18} />
      </span>
      <div>
        <p className="text-2xl font-bold text-slate-950">{value}</p>
        <p className="text-xs font-medium text-slate-500">{label}</p>
      </div>
    </div>
  </article>
);

export default FaceAttendance;
